import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Star, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MarkCompleteAndRate — modal-style block for a single booking.
 *
 * Shows when:
 *   • the booking's `escrow_status === 'held'`  (funds in escrow)
 *   • the caller is the client who booked
 *
 * On submit, releases the escrow and posts a 1-5 star review.
 * Falls back gracefully if the user submits 0 stars (just releases).
 */
export default function MarkCompleteAndRate({ booking, onDone }) {
  const { token, user } = useAuth();
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!booking || done) return null;
  if (booking.escrow_status !== 'held') return null;
  if (user?.id !== booking.client_id) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/escrow/bookings/${booking.id}/mark-complete`,
        {
          booking_id: booking.id,
          stars: stars > 0 ? stars : null,
          review_text: text,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Thanks! Funds released to the provider.');
      setDone(true);
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark complete');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="border-2 border-indigo-300 bg-indigo-50/60 rounded-xl p-5"
      data-testid={`mark-complete-card-${booking.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <CheckCircle2 className="text-indigo-600 flex-shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <h4 className="font-semibold text-slate-900">Service completed?</h4>
          <p className="text-xs text-slate-600">
            Mark this booking complete to release funds to your provider — and leave a quick rating.
            If you don&apos;t respond, funds release automatically 48 hours after the appointment.
          </p>
        </div>
      </div>

      {/* Star picker */}
      <div className="flex items-center gap-1 mb-3" data-testid="rating-stars">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStars(s)}
            onMouseEnter={() => setHover(s)}
            onMouseLeave={() => setHover(0)}
            className="p-0.5"
            data-testid={`rating-star-${s}`}
          >
            <Star
              size={22}
              className={
                (hover || stars) >= s
                  ? 'text-amber-400 fill-amber-400'
                  : 'text-slate-300'
              }
            />
          </button>
        ))}
        <span className="ml-2 text-xs text-slate-500">
          {stars > 0 ? `${stars}/5` : 'Tap a star to rate (optional)'}
        </span>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="Anything to share about the experience? (optional)"
        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-3"
        data-testid="rating-text-input"
        maxLength={2000}
      />

      <button
        onClick={submit}
        disabled={submitting}
        className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium"
        data-testid="mark-complete-submit"
      >
        {submitting ? (
          <>
            <Loader2 className="animate-spin" size={16} /> Submitting…
          </>
        ) : (
          <>
            <CheckCircle2 size={16} /> Mark complete &amp; release funds
          </>
        )}
      </button>
    </div>
  );
}
